import React from 'react';
import { motion } from 'framer-motion';
import { MoreVertical } from 'lucide-react';
import { TUserItem } from '@/data/types';

interface UserListRowProps {
  user: TUserItem;
  index: number;
  getUserInitials: (user: TUserItem) => string;
  getUserRole: (user: TUserItem) => string;
  getUserDepartment: (user: TUserItem) => string;
  getUserStatus: (user: TUserItem) => 'active' | 'inactive';
  getUserProjectsCount: (user: TUserItem) => number;
  getStatusColor: (status: string) => string;
  getDepartmentColor: (department: string) => string;
}

const UserListRow: React.FC<UserListRowProps> = React.memo(
  ({
    user,
    index,
    getUserInitials,
    getUserRole,
    getUserDepartment,
    getUserStatus,
    getUserProjectsCount,
    getStatusColor,
    getDepartmentColor,
  }) => {
    const initials = getUserInitials(user);
    const role = getUserRole(user);
    const department = getUserDepartment(user);
    const status = getUserStatus(user);
    const projectsCount = getUserProjectsCount(user);

    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.02 }}
        className="grid grid-cols-10 gap-4 border-b border-gray-100 px-6 py-4 text-sm transition-colors hover:bg-gray-50"
      >
        <div className="col-span-3 flex items-center gap-3">
          {user.avatar || user.image_url ? (
            <img
              src={user.avatar || user.image_url || ''}
              alt={`${user.first_name} ${user.last_name}`}
              className="h-8 w-8 rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-semibold text-white ${user.avatar || user.image_url ? 'hidden' : 'flex'}`}
          >
            {initials}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {user.first_name} {user.last_name}
            </div>
            <div className="text-gray-500">{user.email}</div>
          </div>
        </div>
        <div className="col-span-2 flex items-center text-gray-600">{role}</div>
        <div className="col-span-2 flex items-center">
          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getDepartmentColor(department)}`}>
            {department}
          </span>
        </div>
        <div className="col-span-2 flex items-center text-gray-600">{user.phone || 'Δεν έχει οριστεί'}</div>
        <div className="col-span-1 flex items-center">
          <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(status)}`}>
            {status === 'active' ? 'Ενεργός' : 'Ανενεργός'}
          </span>
        </div>
      </motion.div>
    );
  },
);

UserListRow.displayName = 'UserListRow';

export default UserListRow;
